import * as React from "react";
import { useState, useEffect } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";

// Block components
import NewsletterImage from "./blocks/NewsletterImage";
import NewsletterHeading from "./blocks/NewsletterHeading";
import NewsletterDescription from "./blocks/NewsletterDescription";
import NewsletterForm from "./blocks/NewsletterForm";
import NewsletterDisclaimer from "./blocks/NewsletterDisclaimer";
import { WaveDividerVertical } from "@src/stories/client/WaveDividerVertical/WaveDividerVertical";
import { WaveDivider } from "@src/stories/client/WaveDivider/WaveDivider";

// Import styles
import './NewsletterSignup.scss';

export const NewsletterSignup = ({ settings }) => {
    const [isSuccess, setIsSuccess] = useState(false);
    const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);

    const { blocks } = settings;
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const customer_posted = params.get('customer_posted');
        if (customer_posted === 'true') {
            setIsSuccess(true);
        }
    }, []);

    // Sort blocks based on sort_order
    const sortedBlocks = [...blocks].sort((a, b) => a.settings.sort_order - b.settings.sort_order);

    // Get layout type from settings
    const layoutType = settings.section.layout_type || 'side-by-side';
    
    // Determine container classes based on layout
    let containerClasses = 'newsletter-signup overflow-hidden';
    
    if (layoutType === 'side-by-side') {
        containerClasses += ' lg:flex-row flex-col';
    } else if (layoutType === 'stacked') {
        containerClasses += ' flex-col';
    } else if (layoutType === 'centered') {
        containerClasses += ' flex-col items-center text-center';
    }
    
    // Add border radius if specified
    if (settings.section.border_radius) {
        containerClasses += ` rounded-[${settings.section.border_radius}px]`;
    }
    
    // Apply border styles if enabled
    const borderStyles = {};
    if (settings.section.enable_border) {
        borderStyles.borderWidth = `${settings.section.border_thickness || 1}px`;
        borderStyles.borderStyle = 'solid';
        borderStyles.borderColor = settings.section.border_color || '#eaeaea';
    } else {
        borderStyles.border = 'none';
    }
    
    // Background and text color styles
    const backgroundStyle = settings.section.background_color ? { backgroundColor: settings.section.background_color } : {};
    const textStyle = settings.section.text_color ? { color: settings.section.text_color } : {};
    
    // Combine all styles
    const combinedStyles = {
        ...borderStyles,
        ...backgroundStyle,
        ...textStyle
    };
    
    // Content alignment class
    const contentAlignmentClass = `justify-${settings.section.content_alignment}`;

    // Find the image block
    const imageBlock = blocks.find(block => block.type === 'image');
    const imagePosition = imageBlock?.settings?.image_position || 'left';
    
    // Determine the overall layout based on image position (when in side-by-side mode)
    const layoutClasses = layoutType === 'side-by-side' && imagePosition === 'right' 
        ? 'lg:flex-row-reverse' 
        : '';

    // Render each block based on its type
    const renderBlock = (block) => {
        switch (block.type) {
            case 'image':
                return <NewsletterImage key={`block-image-${block.id}`} settings={block.settings} />;
            case 'heading':
                return <NewsletterHeading key={`block-heading-${block.id}`} settings={block.settings} isMobile={isMobile} />;
            case 'description':
                return <NewsletterDescription key={`block-description-${block.id}`} settings={block.settings} />;
            case 'form':
                return (
                    <NewsletterForm 
                        key={`block-form-${block.id}`} 
                        settings={block.settings} 
                        isSuccess={isSuccess}
                        setIsSuccess={setIsSuccess}
                        centered={layoutType === 'centered'}
                        sectionId={settings.section_id}
                    />
                );
            case 'disclaimer':
                return <NewsletterDisclaimer key={`block-disclaimer-${block.id}`} settings={block.settings} />;
            default:
                return null;
        }
    };

    // Get content blocks (everything except image)
    const contentBlocks = sortedBlocks.filter(block => block.type !== 'image');
    
    return (
        <div className={`${containerClasses} ${layoutClasses}`} style={combinedStyles}>
            {layoutType === 'side-by-side' ? (
                <>
                    {/* Side-by-side layout */}
                    {imageBlock && (
                        <div className={`newsletter-signup__image-container w-full`}>
                            {renderBlock(imageBlock)}

                            <WaveDividerVertical
                                settings={{
                                    wave_offset: 0,
                                    wave_width: 60,
                                    wave_color: "#ffffff",
                                    wave_opacity: 1,
                                    wave_style: "wavy",
                                    flip_horizontally: false,
                                    flip_vertically: false,
                                    position: "left",
                                    hide_on_mobile: true,
                                    z_index: 1,
                                    render_type: "all",
                                    custom_id: "newsletter-image-divider",
                                    custom_class: "!absolute top-0 right-0 max-lg:hidden",
                                    show_section_borders: false,
                                    background_color: "transparent",
                                }}
                            />

                            <WaveDivider
                                settings={{
                                wave_offset: 0,
                                wave_height: 60,
                                wave_color: "#ffffff",
                                wave_opacity: 1,
                                wave_style: "wavy",
                                flip_horizontally: false,
                                flip_vertically: false,
                                position: "top",
                                hide_on_mobile: false,
                                z_index: 1,
                                render_type: "mobile-only",
                                custom_id: "image-divider-mobile",
                                custom_class: "!absolute bottom-0 lg:hidden",
                                show_section_borders: false,
                                background_color: "transparent",
                                }}
                            />
                        </div>
                    )}
                    
                    <div className={`newsletter-signup__content side-by-side w-full p-5 pt-3 pb-10 lg:p-10 flex flex-1 flex-col ${contentAlignmentClass}`}>
                        {contentBlocks.map(renderBlock)}
                    </div>
                </>
            ) : layoutType === 'centered' ? (
                <>
                    {/* Centered layout - similar to the image layout */}
                    <div className="newsletter-signup__content centered w-full max-w-3xl mx-auto p-5 lg:p-10 flex flex-col items-center">
                        {sortedBlocks.map(renderBlock)}
                    </div>
                </>
            ) : (
                <>
                    {/* Stacked layout */}
                    <div className={`newsletter-signup__content stacked w-full py-16 lg:pb-16 lg:pt-28 flex flex-col ${contentAlignmentClass}`}>
                        {sortedBlocks.map(renderBlock)}
                    </div>
                </>
            )}
        </div>
    );
};

export default NewsletterSignup;
